import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    FormBuilder,
    FormGroup,
    Validators,
    FormsModule,
    ReactiveFormsModule,
} from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import { SwalService } from '../../../../common/services/swal.service';
import { BanksService } from '../../services/banks.service';
import {
    BankDetailDto,
    BranchDetailDto,
    CreateBranchDto,
} from '../../../../core/data/new-dtos/bank.dtos';
import { SharedModule } from '../../../../common/shared.module';
import { BranchModalComponent, BranchModalResult } from '../branch-modal/branch-modal.component';
import { RegionModalComponent, RegionModalResult } from '../region-modal/region-modal.component';
import { RegionsService } from '../../services/regions.service';
import { RegionDto } from '../../../../core/data/new-dtos/region.dtos';
import { AuthService } from '../../../auth/services/auth.service';

@Component({
    selector: 'app-bank-form',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        SharedModule,
        BranchModalComponent,
        RegionModalComponent,
    ],
    templateUrl: './bank-form.component.html',
    styleUrls: ['./bank-form.component.scss'],
    providers: [SwalService],
})
export class BankFormComponent implements OnInit {
    form!: FormGroup;
    isEditMode = false;
    bankId?: number;
    bank?: BankDetailDto;
    branches: BranchDetailDto[] = [];

    // Branch modal
    showBranchModal = false;
    editingBranch?: BranchDetailDto;

    // Regions
    regions: RegionDto[] = [];
    showRegionModal = false;
    editingRegion?: RegionDto;

    constructor(
        private fb: FormBuilder,
        private swal: SwalService,
        private banksService: BanksService,
        private regionsService: RegionsService,
        private auth: AuthService,
        private router: Router,
        private route: ActivatedRoute,
    ) { }

    /** Renaming or deleting the bank itself stays with La Colonial. */
    get canEditBank() {
        return this.auth.isSuperUser;
    }

    ngOnInit() {
        this.form = this.fb.group({
            name: ['', Validators.required],
        });

        const id = this.route.snapshot.paramMap.get('id');
        if (id) {
            this.isEditMode = true;
            this.bankId = +id;
            this.loadBank();
        }
    }

    async loadBank() {
        if (!this.bankId) return;

        this.swal.showLoading('Cargando banco...');
        try {
            this.bank = await lastValueFrom(this.banksService.getBankById(this.bankId));
            this.form.patchValue({ name: this.bank.name });
            this.branches = [...this.bank.branches];
            await this.loadRegions();
            this.swal.dismiss();
        } catch (e) {
            this.swal.error('Error al cargar el banco');
            this.router.navigate(['/banks']);
        }
    }

    async onSubmit() {
        if (this.form.invalid) {
            this.swal.error('Por favor completa todos los campos requeridos.');
            return;
        }

        const formValue = this.form.value;

        this.swal.showLoading(this.isEditMode ? 'Actualizando banco...' : 'Creando banco...');
        try {
            if (this.isEditMode && this.bankId) {
                await lastValueFrom(this.banksService.updateBank(this.bankId, { name: formValue.name }));
                await this.swal.success('Banco actualizado correctamente.');
            } else {
                const newBank = await lastValueFrom(this.banksService.createBank({ name: formValue.name }));
                this.bankId = newBank.id;
                this.isEditMode = true;
                await this.swal.success('Banco creado correctamente. Ahora puedes agregar sucursales.');
            }
        } catch (e) {
            this.swal.error('Error al guardar el banco.');
        }
    }

    cancel() {
        this.router.navigate(['/banks']);
    }

    // Branch modal management
    openAddBranchModal() {
        this.editingBranch = undefined;
        this.showBranchModal = true;
    }

    openEditBranchModal(branch: BranchDetailDto) {
        this.editingBranch = branch;
        this.showBranchModal = true;
    }

    async onBranchModalClosed(result: BranchModalResult) {
        this.showBranchModal = false;

        if (result.action === 'cancel' || !result.data) return;

        if (!this.bankId) {
            this.swal.error('Primero debes guardar el banco.');
            return;
        }

        const dto: CreateBranchDto = result.data;

        this.swal.showLoading(this.editingBranch ? 'Actualizando sucursal...' : 'Creando sucursal...');
        try {
            if (this.editingBranch) {
                const updatedBranch = await lastValueFrom(
                    this.banksService.updateBranch(this.editingBranch.id, dto)
                );
                const index = this.branches.findIndex(b => b.id === this.editingBranch!.id);
                if (index >= 0) {
                    this.branches[index] = updatedBranch;
                }
                await this.swal.success('Sucursal actualizada.');
            } else {
                const newBranch = await lastValueFrom(
                    this.banksService.createBranch(this.bankId, dto)
                );
                this.branches.push(newBranch);
                await this.swal.success('Sucursal creada.');
            }
            this.swal.dismiss();
            this.editingBranch = undefined;
        } catch (e) {
            this.swal.error('Error al guardar la sucursal.');
            this.editingBranch = undefined;
        }
    }

    // Regions management
    private async loadRegions() {
        if (!this.bankId) return;
        try {
            this.regions = await lastValueFrom(this.regionsService.getRegions(this.bankId));
        } catch {
            this.regions = [];
        }
    }

    regionName(regionId?: number | null): string {
        if (!regionId) return 'Sin región';
        return this.regions.find(r => r.id === regionId)?.name ?? 'Sin región';
    }

    openAddRegionModal() {
        this.editingRegion = undefined;
        this.showRegionModal = true;
    }

    openEditRegionModal(region: RegionDto) {
        this.editingRegion = region;
        this.showRegionModal = true;
    }

    async onRegionModalClosed(result: RegionModalResult) {
        const editing = this.editingRegion;
        this.showRegionModal = false;
        this.editingRegion = undefined;

        if (result.action !== 'save' || !result.name || !this.bankId) return;

        this.swal.showLoading(editing ? 'Renombrando región...' : 'Creando región...');
        try {
            if (editing) {
                await lastValueFrom(this.regionsService.updateRegion(editing.id, { name: result.name }));
                await this.swal.success('Región actualizada.');
            } else {
                await lastValueFrom(this.regionsService.createRegion(this.bankId, { name: result.name }));
                await this.swal.success('Región creada.');
            }
            await this.loadRegions();
        } catch (e: any) {
            this.swal.error(e?.error?.error ?? 'No se pudo guardar la región.');
        }
    }

    async deleteRegion(region: RegionDto) {
        const { isConfirmed } = await this.swal.confirm(
            'Las sucursales de esta región quedarán sin región y los usuarios regionales perderán ese alcance.',
            `¿Eliminar ${region.name}?`,
            { confirmButtonText: 'Sí, eliminar' }
        );

        if (!isConfirmed) return;

        this.swal.showLoading('Eliminando región...');
        try {
            await lastValueFrom(this.regionsService.deleteRegion(region.id));
            await this.swal.success('Región eliminada.');
            await this.loadRegions();
            await this.loadBank();
        } catch (e: any) {
            this.swal.error(e?.error?.error ?? 'No se pudo eliminar la región.');
        }
    }

    async deleteBranch(branchId: number) {
        const { isConfirmed } = await this.swal.confirm(
            'Esta acción no se puede deshacer.',
            '¿Eliminar sucursal?',
            { confirmButtonText: 'Sí, eliminar' }
        );

        if (!isConfirmed) return;

        this.swal.showLoading('Eliminando sucursal...');
        try {
            await lastValueFrom(this.banksService.deleteBranch(branchId));
            this.branches = this.branches.filter(b => b.id !== branchId);
            await this.swal.success('Sucursal eliminada.');
        } catch (e) {
            this.swal.error('Error al eliminar la sucursal.');
        }
    }
}
